import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaLocationDot } from "react-icons/fa6";
import { FaSuitcase } from "react-icons/fa";
import { Dropdown, Modal, Spinner } from "react-bootstrap";
import { useNotification } from "../../../../components/toast/NotificationProvider";

import "./card.css";

import Button from "../button/Button";
import IconButton from "./IconButton";
import { API_URL } from "../../../../api";
import ProfileDetails from "./ProfileDetails";
import UpgradeModal from "../upgradenow/upgradenow";
import * as userAction from "../../../../actions/user";
import { useCookies } from "../../../../hooks/useCookies";
import * as messageActon from "../../../../actions/Message";
import { useUserProfile } from "../context/UserProfileContext";
import { useSocket } from "../../../../Context/SockerContext";
import { getProfileMainFullImage } from "../../../../utils/serverUtils";
import { useAppContext } from "../../../../Context/UseContext";
import Loader from "../../../Utility/Loader";
import * as messageAction from "../../../../actions/Message";
import * as matchActions from "../../../../actions/match";

/**
 * @typedef {Object} Profile
 * @description
 *  Shape of a single discovery profile object rendered by this Card.
 *  Most of these fields come directly from the backend Discovery API.
 * 
 * @property {number} id - The unique identifier for the user.
 * @property {number} user_id - The user ID associated with the user.
 * @property {string} first_name - The first name of the user.
 * @property {string} last_name - The last name of the user.
 * @property {string} birthday - The birthday of the user in ISO format.
 * @property {string} hash - A hash string associated with the user.
 * @property {string} extension - The file extension of the user's profile picture.
 * @property {number} type - The type of user.
 * @property {number} location_id - The location ID associated with the user.
 * @property {string} created_at - The creation timestamp of the user record in ISO format.
 * @property {number} job_id - The job ID associated with the user.
 * @property {string} country - The country of the user.
 * @property {string} continent - The continent of the user.
 * @property {string} location_string - The location string of the user.
 * @property {string} job - The job of the user.
 * @property {boolean} like - The like status of the user.
 * @property {number} location_priority - 1 = same city, 2 = same country, 3 = other countries
 * @property {Profile[]} profiles - The profiles of the user.
 * @property {React.Dispatch<React.SetStateAction<Profile[]>>} setProfiles - 
 *                                     The setProfiles function to update the profiles.
 * @property {number} is_loose_match - 1 when this profile is part of Wave 1 "loose" matches
 *                                     (same country, relaxed lifestyle filters).
 * @property {number} compatibility_score - Optional compatibility score from backend
 *                                          (used only for ordering, not rendered here).

 */

/**
 * Card component to display a single discovery profile.
 *
 * Responsibilities:
 * - Renders the main profile hero card (image, name, age, job, location).
 * - Shows actionable icons (chat, like) and connects them to backend actions.
 * - Integrates with wave-based discovery via `wave` (from context) and `is_loose_match` (from backend)
 *   to show informational toasts about what the user is currently seeing.
 *
 * @param {Profile} props - The properties passed to the component.
 * @returns {JSX.Element} The Card component.
 */

export default function Card({ show, likes = false, ...props }) {
  const navigate = useNavigate();
  const image = getProfileMainFullImage(props);

  const cookies = useCookies();
  const cardRef = useRef(null);
  const pathname = window.location.pathname;
  const [liked, setLiked] = useState(props.like);
  const { profiles, setProfiles, removeProfile, wave } = useUserProfile();
  const { setPage } = props;
  const [showDropdown, setShowDropdown] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showSkip, setShowSkip] = useState(true);
  const [showHeart, setShowHeart] = useState(true);
  const { socket } = useSocket();
  const [topNavVisible, setTopNavVisible] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const fetchedData = useRef(false);
  const location = useLocation();
  const { isPremium } = useAppContext();
  const allowRedirectRef = useRef(true);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [skipLoader, setSkipLoader] = useState(false);
  const [likeLoader, setLikeLoader] = useState(false);
  const [chatLoader, setChatLoader] = useState(false);
  const { showNotification } = useNotification();

  // ---- Toast flag keys (per tab / per session) ----
  // We use sessionStorage to ensure informational toasts are only shown once per wave
  // and once for the Wave 1 loose-match explanation, per browser tab.
  const WAVE_TOAST_PREFIX = "mtd_wave_toast_";
  //const COMPAT_TOAST_KEY = "mtd_wave1_loose_compat_toast";

  function getToastFlag(key) {
    if (typeof window === "undefined") return false;
    try {
      return window.sessionStorage.getItem(key) === "1";
    } catch {
      return false;
    }
  }

  function setToastFlag(key) {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(key, "1");
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    allowRedirectRef.current = true;
  }, [props.user_id]);

  //   Unified toast logic:
  // - Wave-level explanation for Wave 2 and Wave 3 (once per wave per tab).
  // - Wave 1 "loose match" explanation when backend marks profiles as loose matches.
  useEffect(() => {
    if (wave == null) return;

    // 1) Wave-level informational toast (once per wave per tab)
    const waveKey = `${WAVE_TOAST_PREFIX}${wave}`;
    if (!getToastFlag(waveKey)) {
      let waveMsg = "";

      // Wave 0 and Wave 1 already feel self-explanatory from the UI,
      // so we currently only show automatic toasts for Wave 2 and 3.
      if (wave === 2) {
        waveMsg = "discover a bigger match pool.";
      } /*TODO: DELETE
      else if (wave === 3) {
        waveMsg =
          "Matches not available under your filters, so we’ve opened results globally with relaxed preferences.";
      } else if (wave === 0 && props.location_priority !== 1) {
        waveMsg =
          "We couldn’t find matches in your city, so we’re now showing compatible profiles from your country.";
      }*/

      if (waveMsg) {
        showNotification("Search expanded", `${waveMsg}`, "expansion");
        setToastFlag(waveKey);
      }
    }
    /*TODO: DELETE
    // 2) Wave 1 – loose match section toast:
    //    Triggered when the backend sets `is_loose_match = 1` for profiles
    //    that come from the same country but with relaxed lifestyle filters.
    if (
      wave === 1 &&
      props.is_loose_match === 1 &&
      !getToastFlag(COMPAT_TOAST_KEY)
    ) {
      showNotification(
        "Smart Match Expansion",
        "No exact matches found — showing the closest compatible profiles.",
        "expansion"
      );

      setToastFlag(COMPAT_TOAST_KEY);
    }*/
  }, [wave, props.is_loose_match]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function onNewMatch() {
      // Prevent redirect to likes tab if a new match notification comes in
      // while the user is still interacting with the current card.
      allowRedirectRef.current = false;
    }

    window.addEventListener("newMatchReceived", onNewMatch);
    return () => {
      window.removeEventListener("newMatchReceived", onNewMatch);
    };
  }, []);

  const Countswipe = (actionType) => {
    const currentCount = parseInt(cookies.getCookie("swipeCount")) || 0;

    if (currentCount >= 10) {
      // Reset count after limit and show upgrade flow for non-skip/like.
      cookies.setCookie("swipeCount", 0, 1);

      // Only show upgrade modal for actions other than skip/like
      if (actionType !== "skip" && actionType !== "like") {
        setShowUpgradeModal(true);
      }
    } else {
      const newCount = currentCount + 1;
      cookies.setCookie("swipeCount", newCount, 1);
    }
  };

  const fetchData = async () => {
    if (fetchedData.current || !props.user_id) return;
    fetchedData.current = true;

    try {
      const bottomNav = document.querySelector("#bottomOptions");

      if (bottomNav) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            setTopNavVisible(entry.isIntersecting);
          });
        });

        observer.observe(bottomNav);
      }

      // Check if this profile is already skipped or liked,
      // and hide the corresponding buttons accordingly.
      /*const [checkMatchResponse, checkLikedResponse] = await Promise.all([
        await userAction.getMatchSkipped(props.user_id),
        await userAction.getMatchLike(props.user_id),
      ]);

      const checkSkippedData = await checkMatchResponse;
      const checkLikedData = await checkLikedResponse;*/
      // ✅ Keep like logic if you still want to hide heart when already liked
      const checkLikedResponse = await userAction.getMatchLike(props.user_id);
      const checkLikedData = await checkLikedResponse;

      setShowSkip(true);
      setShowHeart(!checkLikedData.liked);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleRefetch = () => {
        fetchData();
      };

      // Allows other parts of the app to trigger a refresh of skip/like state
      // for the current profile.
      window.addEventListener("refetchProfiles", handleRefetch);

      return () => {
        window.removeEventListener("refetchProfiles", handleRefetch);
      };
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [props.user_id]);

  async function getRoom() {
    if (isPremium === true) {
      // Premium users can initiate chat directly.
      let body = JSON.stringify({
        participantId: props.user_id,
      });
      const response = await messageActon.createChatRoom(body);
      if (response) {
        if (sessionStorage.getItem("conversationId")) {
          socket?.emit("leave-room", sessionStorage.getItem("conversationId"));
        }
        sessionStorage.setItem("conversationId", response.conversationId);
        navigate(`/user/chat/with/${props.user_id}?token=${encodeURIComponent(props.profile_token || "")}`, {
          state: {
            img: image,
            name: props.first_name,
            recepientId: props.user_id,
            profile_token: props.profile_token,
          },
        });
      }
    } else {
      try {
        // Non-premium users can chat only if there is a mutual match.
        const checkMatchData = await messageActon.getMatchListById(
          props.user_id
        );
        if (!checkMatchData.isMatch) {
          setShowUpgradeModal(true);
          return;
        }
        const response = await messageActon.createChatRoom({
          participantId: props.user_id,
        });
        if (response) {
          if (sessionStorage.getItem("conversationId")) {
            socket?.emit(
              "leave-room",
              sessionStorage.getItem("conversationId")
            );
          }
          sessionStorage.setItem("conversationId", response.conversationId);
          navigate(`/user/chat/with/${props.user_id}?token=${encodeURIComponent(props.profile_token || "")}`, {
            state: {
              img: image,
              name: props.first_name,
              recepientId: props.user_id,
              profile_token: props.profile_token,
            },
          });
        }
      } catch (error) {
        console.error(error);
      }
    }
  }

  async function handleIconButtonClick(type) {
    // ✅ Prevent multiple concurrent actions for the same button.
    if (
      (type === "skip" && skipLoader) ||
      (type === "like" && likeLoader) ||
      (type === "chat" && chatLoader)
    ) {
      return;
    }

    // ✅ Set loader for clicked button
    if (type === "skip") setSkipLoader(true);
    if (type === "like") setLikeLoader(true);
    if (type === "chat") setChatLoader(true);

    try {
      // ✅ Check premium status for chat or undo
      if ((type === "chat" || type === "undo") && !isPremium) {
        setShowUpgradeModal(true);
        return;
      }

      // ✅ Handle chat logic separately
      if (type === "chat") {
        await getRoom();
        return;
      }

      const currentProfileId = props.user_id;

      // ✅ Call API FIRST
      const response = await matchActions.likeOrSkip(type, props.user_id);

      // ✅ If API fails → stop and show error
      if (!response || response?.error) {
        console.error("API failed, user will not be removed");
        return;
      }

      // ✅ If API succeeds → now safely remove the profile
      if (type === "skip" || type === "like") {
        removeProfile(currentProfileId);
      }

      // ✅ Handle undo restore logic
      if (response && type === "undo") {
        const undoUser = response.user;
        const currentProfileIdx = profiles.findIndex(
          (profile) => profile.user_id === currentProfileId
        );
        setProfiles([
          ...profiles.slice(0, currentProfileIdx),
          undoUser,
          ...profiles.slice(currentProfileIdx),
        ]);
      }

      // ✅ Handle swipe counts & limits
      if (type === "skip" || type === "like") {
        if (!isPremium) {
          Countswipe(type);
        }

        if (!isPremium && parseInt(cookies.getCookie("swipeCount")) >= 10) {
          Countswipe(type);
          return;
        }

        // ✅ Handle pagination when only 1 profile is left
        if (
          !window.location.pathname.endsWith(currentProfileId) &&
          profiles?.length === 1
        ) {
          setPage((prev) => prev + 1);
        }

        // ✅ Handle navigation after skip
        if (
          window.location.pathname.endsWith(currentProfileId) &&
          type === "skip"
        ) {
          if (!isPremium && parseInt(cookies.getCookie("swipeCount")) >= 10) {
            Countswipe(type);
            return;
          }
          navigate("/user/home");
        }

        // ✅ Handle navigation after like
        if (
          allowRedirectRef.current &&
          window.location.pathname.endsWith(currentProfileId) &&
          type === "like"
        ) {
          if (!isPremium && parseInt(cookies.getCookie("swipeCount")) >= 10) {
            Countswipe(type);
            return;
          }
           navigate("/user/home");
        }
      }
    } catch (error) {
      console.error("Error handling button click:", error);
    } finally {
      // ✅ Reset only the clicked button's loader
      if (type === "skip") setSkipLoader(false);
      if (type === "like") setLikeLoader(false);
      if (type === "chat") setChatLoader(false);
    }
  }

  return (
    <>
      {loading && <Loader />}
      <UpgradeModal show={showUpgradeModal} setShow={setShowUpgradeModal} />
      <ReportModal
        show={showReportModal}
        setShow={setShowReportModal}
        personId={props.user_id}
      />
      <BlockModal
        show={showBlockModal}
        setShow={setShowBlockModal}
        personId={props.user_id}
      />
      {!loading && (
        <div className="card-and-details-container">
          {/* === Top Bar (chat • Profile • Upgrade • settings) === */}
          <div className="kh-topbar" onClick={(e) => e.stopPropagation()}>
            {/* Center title */}
            {/* Center title with Back Button on Left */}
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              {location.pathname !== "/user/home" && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    window.history.back();
                  }}
                  style={{ cursor: "pointer", display: "flex" }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19 12H5"
                      stroke="#333333"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 19L5 12L12 5"
                      stroke="#333333"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
              <div className="kh-topbar-title">myTamilDate</div>
            </div>

            {/* Right: upgrade + settings */}
            <div className="kh-topbar-right">
              {isPremium === false && (
                <button
                  type="button"
                  className="kh-upgrade-btn mobile-only"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/selectplan"); // or navigate("/selectplan")
                  }}
                  aria-label="Upgrade to Premium"
                >
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 36 36"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.84167 9.06245C8.53827 9.06245 9.10527 9.62945 9.10527 10.326C9.10527 10.7778 8.86767 11.1846 8.46987 11.415C8.28267 11.523 8.21967 11.7624 8.32767 11.9496L12.5829 19.3189C12.6531 19.4395 12.7809 19.5151 12.9213 19.5151C13.0617 19.5151 13.1895 19.4413 13.2597 19.3189L17.5149 11.9479C17.6229 11.7607 17.5599 11.523 17.3727 11.415C16.9749 11.1846 16.7391 10.7779 16.7391 10.3279C16.7391 9.63125 17.3061 9.06425 18.0027 9.06425C18.6993 9.06425 19.2663 9.63125 19.2663 10.3279C19.2663 10.7797 19.0287 11.1865 18.6291 11.4169C18.5391 11.4691 18.4743 11.5536 18.4473 11.6544C18.4203 11.7552 18.4347 11.8614 18.4869 11.9514L22.7457 19.3207C22.8159 19.4412 22.9437 19.5169 23.0841 19.5169C23.2245 19.5169 23.3523 19.4431 23.4225 19.3207L27.6777 11.9514C27.7857 11.7642 27.7209 11.5249 27.5355 11.4169C27.1377 11.1865 26.9001 10.7797 26.9001 10.3279C26.9001 9.63125 27.4671 9.06425 28.1637 9.06425C28.8603 9.06425 29.4273 9.63125 29.4273 10.3279C29.4273 11.0245 28.8603 11.5914 28.1637 11.5914C27.9477 11.5914 27.7731 11.766 27.7731 11.982V23.1493H19.0107C18.7947 23.1493 18.6201 23.3239 18.6201 23.5399C18.6201 23.7559 18.7947 23.9304 19.0107 23.9304H27.7731V26.9328H8.23407V11.9803C8.23407 11.7643 8.05947 11.5896 7.84348 11.5896C7.14688 11.5896 6.57987 11.0226 6.57987 10.326C6.57987 9.62945 7.14507 9.06245 7.84167 9.06245Z"
                      fill="white"
                    />
                  </svg>
                  <span className="kh-upgrade-text">Upgrade</span>
                </button>
              )}
              <button
                type="button"
                className="kh-topbar-icon"
                aria-label="Settings"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/user/preferences");
                }}
              >
                {/* Preferences icon */}
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 5L10 5M10 5C10 6.10457 10.8954 7 12 7C13.1046 7 14 6.10457 14 5M10 5C10 3.89543 10.8954 3 12 3C13.1046 3 14 3.89543 14 5M14 5L20 5M4 12H16M16 12C16 13.1046 16.8954 14 18 14C19.1046 14 20 13.1046 20 12C20 10.8954 19.1046 10 18 10C16.8954 10 16 10.8954 16 12ZM8 19H20M8 19C8 17.8954 7.10457 17 6 17C4.89543 17 4 17.8954 4 19C4 20.1046 4.89543 21 6 21C7.10457 21 8 20.1046 8 19Z"
                    stroke="#333333"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Banner inserted between Top Bar and Card */}
          {props.showIncompleteBanner && (
             <div className="incompleteBanner">
               <div>
                 <strong>Profile Incomplete</strong>
                 <p style={{ margin: 0 }}>Please complete your profile to unlock all features.</p>
               </div>
               <button
                 onClick={() => navigate("/updateprofile")}
                 className="incompleteBannerButton"
               >
                 Complete Profile
               </button>
             </div>
          )}
          <div
            ref={cardRef}
            className={`card-container ${show ? "show" : ""}`}
            style={{
              backgroundImage: `linear-gradient(0.05deg, #000000 -1.48%, rgba(0, 0, 0, 0.1) 29.79%, rgba(0, 0, 0, 0) 99.96%), url(${image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              borderRadius: "24px",
              overflow: "hidden",
            }}
          >
            <div className="details-container" style={{ width: "100%" }}>
              <div
                className="details"
                style={{ marginLeft: "1rem", marginBottom: "1rem" }}
              >
                <p style={{ marginBottom: "-10px" }}>
                  {" "}
                  <span
                    style={{
                      fontSize: "32px",
                      fontWeight: "600",
                      lineHeight: "48px",
                    }}
                  >{`${props.first_name}`}</span>
                  ,{" "}
                  <span
                    style={{
                      fontSize: "32px",
                      fontWeight: "300",
                      lineHeight: "48px",
                    }}
                  >
                    {dayjs().diff(props.birthday, "y")}
                  </span>
                </p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "0.5rem",
                    marginBottom: "-10px",
                  }}
                >
                  <FaSuitcase size={25} />
                  <p
                    style={{
                      fontSize: "medium",
                    }}
                  >
                    {props.job}
                  </p>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "0.5rem",
                  }}
                >
                  <FaLocationDot size={25} />
                  <p
                    style={{
                      fontSize: "medium",
                    }}
                  >
                    {props.location_string
                      ? `${props.location_string}, ${props.country}`
                      : `${props.country}`}
                  </p>
                </div>
              </div>
              {pathname !== "/preview" && (
                <div className="button-container">
                  <div className="left-buttons">
                    {chatLoader ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        role="status"
                        className="mt-2"
                      />
                    ) : (
                      <IconButton
                        type="chat"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleIconButtonClick("chat");
                        }}
                      />
                    )}
                  </div>
                  <div className="right-buttons">
                    {showHeart && (
                      <>
                        {likeLoader ? (
                          <Spinner
                            animation="border"
                            size="sm"
                            role="status"
                            className="mt-2"
                          />
                        ) : (
                          <IconButton
                            type={"likeActive"}
                            onClick={(e) => {
                              e.stopPropagation();
                              setLiked(!liked);
                              handleIconButtonClick("like");
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div
              className="menu"
              style={{
                flexDirection: "row !important",
                justifyContent: "space-between",
                width: `${location.pathname !== "/user/home" ? "93%" : ""}`,
              }}
            >


              <Dropdown
                className="discovery-profile-preferences-dropdown"
                show={showDropdown}
                onToggle={() => setShowDropdown(!showDropdown)}
                onMouseEnter={() => setShowDropdown(true)}
                onMouseLeave={() => setShowDropdown(false)}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/user/preferences");
                }}
                style={{
                  cursor: "pointer",
                }}
              >
                <Dropdown.Toggle as="div">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 5L10 5M10 5C10 6.10457 10.8954 7 12 7C13.1046 7 14 6.10457 14 5M10 5C10 3.89543 10.8954 3 12 3C13.1046 3 14 3.89543 14 5M14 5L20 5M4 12H16M16 12C16 13.1046 16.8954 14 18 14C19.1046 14 20 13.1046 20 12C20 10.8954 19.1046 10 18 10C16.8954 10 16 10.8954 16 12ZM8 19H20M8 19C8 17.8954 7.10457 17 6 17C4.89543 17 4 17.8954 4 19C4 20.1046 4.89543 21 6 21C7.10457 21 8 20.1046 8 19Z"
                      stroke="#FFFFFF"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    border: "none",
                  }}
                >
                  <Dropdown.Item
                    as="button"
                    className="dropdown-hover-button"
                    style={{
                      color: "white",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/user/preferences");
                    }}
                  >
                    Preferences
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
          <div>
            <ProfileDetails
              userId={props.user_id}
              token={props.profile_token}
              handleIconButtonClick={handleIconButtonClick}
              liked={liked}
              setLiked={setLiked}
              activeTabs={"card"}
              showSkip={showSkip}
              showHeart={showHeart}
              skipLoader={skipLoader}
              likeLoader={likeLoader}
              chatLoader={chatLoader}
            />
          </div>
        </div>
      )}
    </>
  );
}

function ReportModal({ show, setShow, personId }) {
  const cookies = useCookies();
  const [options, setOptions] = useState([]);
  const formRef = useRef(null);
  const fetchedReasons = useRef(false);

  useEffect(() => {
    if (!fetchedReasons.current) {
      fetchedReasons.current = true;
      (async () => {
        try {
          const response = await messageAction.getReportReason();
          if (!response) throw new Error("Failed to fetch report reasons");

          if (response) {
            setOptions(response);
          }
        } catch (error) {
          console.error("Error fetching report reasons:", error);
        }
      })();
    }
  }, [personId]);

  async function report(e) {
    e.preventDefault();
    const body = JSON.stringify({
      personId: personId,
      ...Object.fromEntries(new FormData(formRef.current)),
    });
    const response = await messageAction.updateReportReason(body);
    if (response) {
      window.location.reload();
    }
  }

  return (
    <Modal
      className="customer-modal-without-header-footer"
      size="sm"
      centered
      show={show}
    >
      <Modal.Body>
        <p
          style={{
            fontSize: "large",
            fontWeight: "600",
            margin: "0",
            marginBottom: "1rem",
            color: "#6c6c6c",
          }}
        >
          Report user?
        </p>
        <p
          style={{
            fontSize: "14px",
            margin: "0",
            textAlign: "center",
            color: "#6c6c6c",
          }}
        >
          Report this user for violating MTD's community guidelines. Please
          select a reason for reporting the user below to help us improve your
          experience.
        </p>
        <form
          ref={formRef}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            marginBlock: "1rem",
          }}
          onSubmit={report}
        >
          {options.map((option) => (
            <label
              key={option.id}
              htmlFor={option.id}
              style={{
                display: "flex",
                gap: "1rem",
                alignItems: "center",
                justifyContent: "flex-start",
                fontSize: "14px",
                textAlign: "left",
                color: "#6c6c6c",
              }}
            >
              <input
                type="radio"
                name="reportId"
                id={option.id}
                value={option.id}
                style={{
                  accentColor: "#4E1173",
                  width: "20px",
                  height: "20px",
                }}
              />
              <span>{option.name}</span>
            </label>
          ))}
        </form>
        <div
          style={{
            marginTop: "1rem",
            display: "flex",
            gap: "1rem",
            marginInline: "auto",
          }}
        >
          <button
            type="button"
            style={{
              borderRadius: "9999px",
              padding: "0.75rem 1.5rem",
              border: "2px solid #6c6c6c",
              color: "#6c6c6c",
              backgroundColor: "transparent",
            }}
            onClick={() => setShow(false)}
          >
            Close
          </button>
          <Button
            type="submit"
            style={{
              borderRadius: "9999px",
              padding: "0.75rem 1.5rem",
            }}
            onClick={() => formRef.current?.requestSubmit()}
          >
            Submit
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function BlockModal({ show, setShow, personId }) {
  const cookies = useCookies();

  async function block() {
    const response = await fetch(`${API_URL}customer/matches/block`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${cookies.getCookie("token")}`,
      },
      body: JSON.stringify({
        personId: personId,
      }),
    });
    if (response.ok) {
      window.location.reload();
    }
  }

  return (
    <Modal size="sm" centered show={show}>
      <Modal.Body>
        <p
          style={{
            fontSize: "large",
            fontWeight: "600",
            margin: "0",
            marginBottom: "1rem",
            color: "#6c6c6c",
          }}
        >
          Block user
        </p>
        <p
          style={{
            fontSize: "14px",
            margin: "0",
            textAlign: "center",
            color: "#6c6c6c",
          }}
        >
          Blocked members will no longer be able to message you and won't show
          up when you're browsing
        </p>
        <div
          style={{
            marginTop: "1rem",
            display: "flex",
            gap: "1rem",
            marginInline: "auto",
          }}
        >
          <button
            type="button"
            style={{
              borderRadius: "9999px",
              padding: "0.75rem 1.5rem",
              border: "2px solid #6c6c6c",
              color: "#6c6c6c",
              backgroundColor: "transparent",
            }}
            onClick={() => setShow(false)}
          >
            Close
          </button>
          <Button
            onClick={block}
            style={{
              borderRadius: "9999px",
              padding: "0.75rem 1.5rem",
            }}
          >
            Submit
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export function ChatReqeustRejected({ chatshow, setChatshow }) {
  return (
    <Modal
      centered
      className="customer-modal-without-header-footer selfie-modal"
      show={chatshow}
      onHide={() => setChatshow(false)}
    >
      <Modal.Body
        className="selfie-modal-body"
        style={{
          gap: "0px",
        }}
      >
        You can only chat with person who have matched with you.
        <div style={{ marginTop: "65px" }}>
          <button
            type="submit"
            className="global-save-button"
            onClick={() => setChatshow(false)}
          >
            Okay
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

/**
 * Utility to clear toast flags from sessionStorage.
 *
 * Call this when you want to "reset" wave/location/compatibility toasts
 * for the current browser tab (e.g. after logout or when starting a new session).
 */
export function resetToastFlags() {
  if (typeof window === "undefined") return;

  try {
    // Remove all wave toast flags
    for (let key in window.sessionStorage) {
      if (key.startsWith("mtd_wave_toast_")) {
        window.sessionStorage.removeItem(key);
      }
    }
    /* TODO: DELETE
    // Remove Wave 1 loose-match compatibility toast flag
    if (window.sessionStorage.getItem("mtd_wave1_loose_compat_toast")) {
      window.sessionStorage.removeItem("mtd_wave1_loose_compat_toast");
    }*/
    console.log("🔥 Wave + Loose-match toast flags reset.");
  } catch (e) {
    console.error("Error resetting toast flags:", e);
  }
}
